import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Link2, Unlink2 } from 'lucide-react';

/**
 * Available CSS units for compact mode
 */
const COMPACT_UNITS = ['px', 'rem', '%'];

/**
 * Unit conversion utilities
 */
const unitConversions = {
  toPx: {
    'px': (v) => v,
    'rem': (v) => v * 16,
    'em': (v) => v * 16,
    'vh': (v) => v * (typeof window !== 'undefined' ? window.innerHeight / 100 : 1),
    'vw': (v) => v * (typeof window !== 'undefined' ? window.innerWidth / 100 : 1),
    '%': (v) => v
  },
  fromPx: {
    'px': (v) => v,
    'rem': (v) => v / 16,
    'em': (v) => v / 16,
    'vh': (v) => v / (typeof window !== 'undefined' ? window.innerHeight / 100 : 1),
    'vw': (v) => v / (typeof window !== 'undefined' ? window.innerWidth / 100 : 1),
    '%': (v) => v
  }
};

/**
 * Convert value between units
 */
function convertValue(value, fromUnit, toUnit) {
  // Don't convert 'auto'
  if (value === 'auto') return value;
  if (value === 0 || value === '0') return 0;

  const numValue = parseFloat(value);
  if (isNaN(numValue)) return 0;

  const pxValue = unitConversions.toPx[fromUnit]?.(numValue) ?? numValue;
  const convertedValue = unitConversions.fromPx[toUnit]?.(pxValue) ?? pxValue;

  return Math.round(convertedValue * 100) / 100;
}

/**
 * Available CSS units
 */
const UNITS = [
  { value: 'px', label: 'px' },
  { value: 'rem', label: 'rem' },
  { value: 'em', label: 'em' },
  { value: 'vh', label: 'vh' },
  { value: 'vw', label: 'vw' },
  { value: '%', label: '%' },
];

/**
 * SpacingField - 4-value spacing input for padding/margin
 *
 * Supports:
 * - Individual values for top, bottom, left, right
 * - Unit selection with automatic conversion
 * - 'auto' support for margin left/right
 *
 * @param {Object} props
 * @param {Object} props.field - Field configuration
 * @param {string} props.field.key - Unique field identifier
 * @param {string} props.field.label - Display label
 * @param {string} props.field.type - 'padding', 'margin', 'paddingFull', or 'marginFull'
 * @param {Object} [props.field.default] - Default spacing object
 * @param {Object} props.value - Current spacing value { top, bottom, left, right, unit }
 * @param {Function} props.onChange - Callback when value changes
 * @param {boolean} [props.disabled] - Whether field is disabled
 * @param {boolean} [props.collapsible] - Whether to show as collapsible section (default: true)
 * @param {boolean} [props.defaultCollapsed] - Initial collapsed state (default: false)
 * @param {boolean} [props.compact] - Use compact inline layout (default: false)
 */
export default function SpacingField({
  field,
  value,
  onChange,
  disabled = false,
  collapsible = true,
  defaultCollapsed = false,
  compact = false,
}) {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const [isLinked, setIsLinked] = useState(false);

  // Determine if this is a margin field (supports 'auto')
  const isMargin = field.type === 'margin' || field.type === 'marginFull';

  // Normalize value to object format
  const normalizeValue = (val) => {
    if (typeof val === 'object' && val !== null) {
      return {
        top: val.top ?? 0,
        bottom: val.bottom ?? 0,
        left: val.left ?? 0,
        right: val.right ?? 0,
        unit: val.unit || 'px'
      };
    }
    if (typeof val === 'number') {
      return { top: val, bottom: val, left: val, right: val, unit: 'px' };
    }
    return field.default || { top: 0, bottom: 0, left: 0, right: 0, unit: 'px' };
  };

  const spacingObj = normalizeValue(value);

  const updateSide = (side, newValue) => {
    let processedValue;
    const trimmedValue = String(newValue).trim().toLowerCase();

    if (isMargin && trimmedValue === 'auto') {
      processedValue = 'auto';
    } else if (trimmedValue === '' || trimmedValue === '-') {
      // Allow empty or just minus sign during typing
      processedValue = trimmedValue === '-' ? '-' : 0;
    } else {
      const parsed = parseFloat(newValue);
      processedValue = isNaN(parsed) ? 0 : parsed;
    }

    onChange({ ...spacingObj, [side]: processedValue });
  };

  const updateUnit = (newUnit) => {
    const oldUnit = spacingObj.unit;

    const newSpacing = {
      top: convertValue(spacingObj.top, oldUnit, newUnit),
      bottom: convertValue(spacingObj.bottom, oldUnit, newUnit),
      left: convertValue(spacingObj.left, oldUnit, newUnit),
      right: convertValue(spacingObj.right, oldUnit, newUnit),
      unit: newUnit
    };

    onChange(newSpacing);
  };

  const toggleAuto = (side) => {
    const currentValue = spacingObj[side];
    updateSide(side, currentValue === 'auto' ? '0' : 'auto');
  };

  // Handle arrow key increments/decrements for text inputs
  const handleKeyDown = (e, side) => {
    const currentValue = spacingObj[side];
    if (currentValue === 'auto' || currentValue === '-') return;

    const parsed = parseFloat(currentValue);
    const numValue = isNaN(parsed) ? 0 : parsed;
    const step = e.shiftKey ? 10 : 1;

    if (e.key === 'ArrowUp') {
      e.preventDefault();
      updateSide(side, numValue + step);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      updateSide(side, numValue - step);
    }
  };

  // Update all sides at once (for linked mode)
  const updateAllSides = (newValue) => {
    const trimmedValue = String(newValue).trim().toLowerCase();
    let processedValue;

    if (trimmedValue === '' || trimmedValue === '-') {
      processedValue = trimmedValue === '-' ? '-' : 0;
    } else {
      const parsed = parseFloat(newValue);
      processedValue = isNaN(parsed) ? 0 : parsed;
    }

    onChange({
      ...spacingObj,
      top: processedValue,
      right: processedValue,
      bottom: processedValue,
      left: processedValue
    });
  };

  // Compact mode: visual box model editor
  if (compact) {
    const isAutoLR = spacingObj.left === 'auto' && spacingObj.right === 'auto';

    const handleSpacingKeyDown = (e, side) => {
      if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
      const currentVal = spacingObj[side];
      if (currentVal === 'auto' || currentVal === '-') return;
      e.preventDefault();
      const step = e.shiftKey ? 10 : 1;
      const delta = e.key === 'ArrowUp' ? step : -step;
      const parsed = parseFloat(currentVal);
      const newVal = (isNaN(parsed) ? 0 : parsed) + delta;

      if (isLinked) {
        updateAllSides(newVal);
      } else {
        updateSide(side, newVal);
      }
    };

    const handleSideChange = (side, rawValue) => {
      const trimmed = String(rawValue).trim().toLowerCase();
      if (isMargin && (side === 'left' || side === 'right') && (trimmed === 'a' || trimmed === 'auto')) {
        updateSide(side, 'auto');
        return;
      }
      if (isLinked) {
        updateAllSides(rawValue);
      } else {
        updateSide(side, rawValue);
      }
    };

    const toggleAutoLR = () => {
      if (isAutoLR) {
        onChange({ ...spacingObj, left: 0, right: 0 });
      } else {
        onChange({ ...spacingObj, left: 'auto', right: 'auto' });
      }
    };

    const renderSideInput = (side) => {
      const sideValue = spacingObj[side];
      const isAuto = sideValue === 'auto';

      return (
        <input
          type="text"
          inputMode={isAuto ? 'text' : 'numeric'}
          value={isAuto ? 'auto' : (sideValue != null ? sideValue : '')}
          placeholder="0"
          onChange={(e) => handleSideChange(side, e.target.value)}
          onKeyDown={(e) => handleSpacingKeyDown(e, side)}
          disabled={disabled}
          className={`w-14 px-1 py-0.5 text-[11px] text-center border rounded
            bg-white border-gray-200
            focus:border-blue-400 focus:ring-1 focus:ring-blue-400 focus:outline-none
            ${isAuto ? 'text-indigo-600 font-medium text-[10px]' : 'text-gray-700'}
            ${disabled ? 'bg-gray-100 cursor-not-allowed opacity-50' : ''}`}
        />
      );
    };

    const typeLabel = isMargin ? 'margin' : 'padding';

    return (
      <div className="space-y-1.5">
        {/* Label */}
        <label className="text-xs text-gray-500 truncate">
          {field.label}
        </label>

        {/* Box model visualization */}
        <div className="relative border border-gray-200 rounded-lg p-3 bg-white">
          {/* Top input */}
          <div className="flex justify-center mb-1.5">
            {renderSideInput('top')}
          </div>

          {/* Middle row: left input, inner box, right input */}
          <div className="flex items-center justify-between gap-1.5">
            <div className="flex-shrink-0">
              {renderSideInput('left')}
            </div>
            <div className="flex-1 flex items-center justify-center bg-gray-50 border border-gray-200 rounded py-3 min-h-[36px]">
              <span className="text-gray-400 text-[10px] uppercase tracking-wider select-none">
                {typeLabel}
              </span>
            </div>
            <div className="flex-shrink-0">
              {renderSideInput('right')}
            </div>
          </div>

          {/* Bottom input */}
          <div className="flex justify-center mt-1.5">
            {renderSideInput('bottom')}
          </div>
        </div>

        {/* Controls row: link toggle, unit selector, auto button */}
        <div className="flex items-center gap-1.5">
          {/* Link/Unlink toggle */}
          <button
            type="button"
            onClick={() => setIsLinked(!isLinked)}
            disabled={disabled}
            title={isLinked ? 'Unlink sides (edit independently)' : 'Link sides (edit all at once)'}
            className={`p-1.5 rounded border transition-colors
              ${isLinked
                ? 'bg-blue-50 border-blue-300 text-blue-600'
                : 'bg-white border-gray-200 text-gray-400 hover:text-gray-600 hover:border-gray-300'
              }
              ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {isLinked ? (
              <Link2 className="w-3.5 h-3.5" />
            ) : (
              <Unlink2 className="w-3.5 h-3.5" />
            )}
          </button>

          {/* Unit selector */}
          <select
            value={spacingObj.unit}
            onChange={(e) => updateUnit(e.target.value)}
            disabled={disabled}
            className={`px-1.5 py-1 text-xs border rounded bg-white border-gray-200
              focus:border-blue-400 focus:ring-1 focus:ring-blue-400
              ${disabled ? 'bg-gray-100 cursor-not-allowed opacity-50' : ''}`}
          >
            {COMPACT_UNITS.map((unit) => (
              <option key={unit} value={unit}>{unit}</option>
            ))}
          </select>

          {/* Auto L+R button (margin only) */}
          {isMargin && (
            <button
              type="button"
              onClick={toggleAutoLR}
              disabled={disabled}
              title={isAutoLR ? 'Remove auto from left and right' : 'Set left and right to auto (center)'}
              className={`ml-auto px-2 py-1 text-[11px] font-medium rounded border transition-colors
                ${isAutoLR
                  ? 'bg-indigo-500 text-white border-indigo-600'
                  : 'bg-white text-gray-500 border-gray-200 hover:text-gray-700 hover:border-gray-300'
                }
                ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              Auto L+R
            </button>
          )}
        </div>
      </div>
    );
  }

  const renderContent = () => (
    <div className="space-y-2">
      {/* Unit selector */}
      <div>
        <label className="block text-xs text-gray-600 mb-1">Unit</label>
        <select
          value={spacingObj.unit}
          onChange={(e) => updateUnit(e.target.value)}
          disabled={disabled}
          className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
        >
          {UNITS.map((unit) => (
            <option key={unit.value} value={unit.value}>
              {unit.label}
            </option>
          ))}
        </select>
      </div>

      {/* Spacing inputs in 2x2 grid */}
      <div className="grid grid-cols-2 gap-2">
        {/* Top */}
        <div>
          <label className="block text-xs text-gray-600 mb-1">Top</label>
          <input
            type="text"
            inputMode="decimal"
            value={spacingObj.top}
            onChange={(e) => updateSide('top', e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, 'top')}
            disabled={disabled}
            className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            placeholder="0"
          />
        </div>

        {/* Bottom */}
        <div>
          <label className="block text-xs text-gray-600 mb-1">Bottom</label>
          <input
            type="text"
            inputMode="decimal"
            value={spacingObj.bottom}
            onChange={(e) => updateSide('bottom', e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, 'bottom')}
            disabled={disabled}
            className="w-full px-2 py-1 text-xs border border-gray-300 rounded"
            placeholder="0"
          />
        </div>

        {/* Left */}
        <div>
          <label className="block text-xs text-gray-600 mb-1">Left</label>
          <input
            type="text"
            inputMode="decimal"
            value={spacingObj.left === 'auto' ? 'auto' : spacingObj.left}
            onChange={(e) => updateSide('left', e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, 'left')}
            disabled={disabled || spacingObj.left === 'auto'}
            className="w-full px-2 py-1 text-xs border border-gray-300 rounded disabled:bg-gray-100 disabled:text-gray-600"
            placeholder="0"
          />
          {isMargin && (
            <button
              type="button"
              onClick={() => toggleAuto('left')}
              disabled={disabled}
              className={`w-full mt-1 px-2 py-1 text-xs rounded border transition-colors ${
                spacingObj.left === 'auto'
                  ? 'bg-indigo-500 text-white border-indigo-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {spacingObj.left === 'auto' ? '✓ Auto' : 'Set Auto'}
            </button>
          )}
        </div>

        {/* Right */}
        <div>
          <label className="block text-xs text-gray-600 mb-1">Right</label>
          <input
            type="text"
            inputMode="decimal"
            value={spacingObj.right === 'auto' ? 'auto' : spacingObj.right}
            onChange={(e) => updateSide('right', e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, 'right')}
            disabled={disabled || spacingObj.right === 'auto'}
            className="w-full px-2 py-1 text-xs border border-gray-300 rounded disabled:bg-gray-100 disabled:text-gray-600"
            placeholder="0"
          />
          {isMargin && (
            <button
              type="button"
              onClick={() => toggleAuto('right')}
              disabled={disabled}
              className={`w-full mt-1 px-2 py-1 text-xs rounded border transition-colors ${
                spacingObj.right === 'auto'
                  ? 'bg-indigo-500 text-white border-indigo-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {spacingObj.right === 'auto' ? '✓ Auto' : 'Set Auto'}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  if (!collapsible) {
    return (
      <div>
        <label className="block text-xs font-medium text-gray-700 mb-2">
          {field.label}
        </label>
        {renderContent()}
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded">
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <label className="text-xs font-medium text-gray-700 cursor-pointer">
          {field.label}
        </label>
        {isCollapsed ? (
          <ChevronDown className="w-4 h-4 text-gray-500" />
        ) : (
          <ChevronUp className="w-4 h-4 text-gray-500" />
        )}
      </button>
      {!isCollapsed && (
        <div className="p-3">
          {renderContent()}
        </div>
      )}
    </div>
  );
}
